import auth from '@/middleware/auth'
import logout from '@/middleware/logout'
import internalAdmin from '@/middleware/internalAdmin'
import tenantAdmin from '@/middleware/tenantAdmin'
import activityAccess from '@/middleware/activityAccess'
import AuthLayout from '@/layouts/AuthLayout.vue'
import dayjs from "dayjs"
import { h } from 'vue'

const routes = [
  {
    path: '/test-page',
    name: 'test-page',
    component: () => import('@/views/TestPage.vue'),
  },
  {
    path: '/vsign-success',
    name: 'vsign-success',
    component: () => import('@/views/VSignSuccess.vue'),
  },
  {
    path: '/redirect',
    redirect: () => {
      const path = sessionStorage.getItem('outlookRedirect')
      if (!path) {
        sessionStorage.removeItem('outlookRedirect')
        return { name: 'home' }
      }

      sessionStorage.removeItem('outlookRedirect')
      return { path }
    },
  },
  {
    path: '/',
    component: AuthLayout,
    children: [
      {
        path: '/login',
        name: 'login',
        component: () => import('@/views/Auth/Login.vue'),
      },
      {
        path: '/password-forgot',
        name: 'password-forgot',
        component: () => import('@/views/Auth/ForgotPassword.vue'),
      },
      {
        path: '/password-reset',
        name: 'password-reset',
        component: () => import('@/views/Auth/ResetPassword.vue'),
      },
      {
        path: '/account-email-change/approve',
        name: 'account-email-change-approve',
        component: () => import('@/views/Auth/EmailChangeApprove.vue'),
      },
      {
        path: '/password-create',
        name: 'password-create',
        props: {
          isCreate: true,
        },
        component: () => import('@/views/Auth/ResetPassword.vue'),
      },
      {
        path: '/create-account',
        name: 'create-account',
        beforeEnter(to, from, next) {
          if (to.query.company) {
            next({ name: 'company-create-account', params: {company: to.query.company}, query: to.query })
          } else {
            next({ name: 'login' })
          }
        },
      },
      {
        path: '/:company',
        component: () => import('@/views/Auth/CompanyAuth.vue'),
        redirect: { name: 'company-login' },
        children: [
          {
            path: 'login',
            name: 'company-login',
            component: () => import('@/views/Auth/Login.vue'),
          },
          {
            path: 'password-forgot',
            name: 'company-password-forgot',
            component: () => import('@/views/Auth/ForgotPassword.vue'),
          },
          {
            path: 'password-reset',
            name: 'company-password-reset',
            component: () => import('@/views/Auth/ResetPassword.vue'),
          },
          {
            path: 'create-account',
            name: 'company-create-account',
            component: () => import('@/views/Auth/CreateAccount.vue'),
          },
        ],
      },
    ],
    beforeEnter() {
      const metaTheme = document.getElementById('theme-color-id')
      if (metaTheme) {
        return
      }
      const meta = document.createElement('meta', {
        id: 'theme-color-id',
        name: "theme-color",
        content: "#f3f4fb",
      })
      meta.id = 'theme-color-id'
      meta.name = 'theme-color'
      meta.content = '#f3f4fb'
      document.head.appendChild(meta)
    },
    beforeLeave() {
      const metaTheme = document.getElementById('theme-color-id')
      document.head.removeChild(metaTheme)
    },
    redirect: { name: 'login' },
  },
  {
    path: '/logout',
    name: 'logout',
    meta: {
      middleware: logout,
    },
  },
  {
    path: '/',
    component: () => import('@/layouts/HomeLayout.vue'),
    middleware: auth,
    children: [
      {
        path: 'home',
        name: 'home',
        component: () => import('@/views/Home/index.vue'),
        meta: {
          middleware: auth,
          pageTitle: 'Command Center',
        },
      },
      {
        path: 'activity',
        name: 'activity',
        component: () => import('@/views/Activity/index.vue'),
        meta: {
          middleware: activityAccess,
          pageTitle: 'Activity',
        },
      },
      {
        path: 'tenant-admin/:tenant',
        name: 'tenant-admin',
        component: () => import('@/views/TenantAdmin/TenantAdminListing.vue'),
        meta: {
          middleware: tenantAdmin,
          pageTitle: 'Tenant Admin',
        },
      },
      {
        path: 'tenant-admin/:tenant/users/create',
        name: 'tenant-admin-create',
        component: () => import('@/views/TenantAdmin/TenantAdminForm.vue'),
        meta: {
          middleware: tenantAdmin,
          pageTitle: 'Create User',
        },
      },
      {
        path: 'tenant-admin/:tenant/users/:user_id(\\d+)/edit',
        name: 'tenant-admin-edit',
        component: () => import('@/views/TenantAdmin/TenantAdminForm.vue'),
        meta: {
          middleware: tenantAdmin,
          pageTitle: 'Edit User',
        },
      },
      // TEMPORARY: redirects old URLs — remove next release. See router/legacyRouteMigrations.js.
      {
        path: 'tenant-admin/:tenant/users/:user_id(\\d+)',
        redirect: (to) => ({ name: 'tenant-admin-edit', params: to.params }),
      },
    ],
  },
  {
    path: '/:company',
    component: () => import('@/layouts/Default.vue'),
    redirect: { name: 'pre-orders' },
    middleware: auth,
    beforeEnter(to, from, next) {
      try {
        const recentWorkflows = JSON.parse(localStorage.getItem('recentWorkflows')) || []
        const currentDate = dayjs().toISOString()
        const existingIndex = recentWorkflows.findIndex(item => item.slug === to.params.company)
        if (existingIndex !== -1) {
          recentWorkflows[existingIndex].date = currentDate
        } else {
          recentWorkflows.push({ slug: to.params.company, date: currentDate })
        }
        localStorage.setItem('recentWorkflows', JSON.stringify(recentWorkflows))
      } catch {
        localStorage.setItem('recentWorkflows', JSON.stringify([]))
      }
      next()
    },
    children: [
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/views/Dashboard/index.vue'),
        meta: {
          middleware: auth,
          pageTitle: 'Dashboard',
        },
      },
      {
        path: 'chat',
        name: 'chat-agent',
        component: () => import('@/views/ChatAgent/index.vue'),
        meta: {
          middleware: auth,
          // The conversation is the whole page; the layout's own title bar would
          // only repeat the agent name the page already shows.
          hideTopbarTitle: true,
        },
      },
      {
        path: 'products/:productSlug',
        component: () => import('@/views/Products/ProductsViews.vue'),
        children: [
          {
            path: '',
            component: () => import('@/views/Products/index.vue'),
            name: 'products',
            meta: {
              middleware: auth,
              customMobileHeader: true,
            },
          },
          {
            path: 'views/:view_id(\\d+)',
            name: 'products-view',
            component: () => import('@/views/Products/index.vue'),
            meta: {
              middleware: auth,
              customMobileHeader: true,
            },
          },
          {
            path: 'views/settings',
            name: 'products-view-settings',
            component: () => import('@/views/Products/Settings.vue'),
            meta: {
              middleware: auth,
              pageTitle: 'Customize Tabs',
            },
          },
        ],
      },
      {
        path: 'pre-orders',
        component: () => import('@/views/Records/RecordsViews.vue'),
        meta: {
          middleware: auth,
        },
        children: [
          {
            path: '',
            name: 'pre-orders',
            component: () => import('@/views/Records/index.vue'),
            props: { isPersist: true },
            meta: {
              middleware: auth,
              pageTitle: 'Records',
              customMobileHeader: true,
            },
          },
          {
            path: 'views/settings',
            name: 'pre-orders-saved-view-settings',
            component: () => import('@/views/Records/Settings.vue'),
            meta: {
              middleware: auth,
              pageTitle: 'Customize Tabs',
            },
          },
          {
            path: 'views/:view_id(\\d+)',
            name: 'pre-orders-saved-view',
            component: () => import('@/views/Records/index.vue'),
            props: { isPersist: false },
            meta: {
              middleware: auth,
              customMobileHeader: true,
            },
          },
          {
            path: 'views',
            redirect: { name: 'pre-orders' },
          },
        ],
      },
      {
        path: 'resources',
        name: 'resources',
        component: () => import('@/views/Resources/index.vue'),
        meta: {
          middleware: auth,
          pageTitle: 'Resources',
          pageDescription: 'Manage your organization’s resources here.',
        },
      },
      {
        path: 'resources/:resource_id(\\d+)',
        name: 'resource',
        component: () => import('@/views/Resources/index.vue'),
        meta: {
          middleware: auth,
          pageTitle: 'Resources',
          pageDescription: 'Manage your organization’s resources here.',
        },
      },
      {
        path: 'pre-orders/:order_id(\\d+)',
        name: 'pre-order',
        component: () => import('@/views/Record/index.vue'),
        meta: {
          middleware: auth,
        },
      },
      {
        path: 'admin',
        component: () => import('@/views/Admin/index.vue'),
        meta: {
          middleware: [internalAdmin],
        },
        children: [
          {
            path: '',
            name: 'admin-panel',
            component: () => import('@/views/Admin/views/index.vue'),
            meta: {
              pageTitle: 'Admin',
            },
          },
          {
            path: 'v-ai',
            name: 'v-ai-admin-panel',
            component: () => import('@/views/Admin/views/vAI/index.vue'),
            meta: {
              pageTitle: 'vAI Admin',
            },
          },
          {
            path: 'v-ai/file-parsing',
            name: 'v-ai-admin-panel-file-parsing',
            component: () => import('@/views/Admin/views/vAI/VAIFileParsing.vue'),
            meta: {
              pageTitle: 'AI File Parsing',
            },
          },
          {
            path: 'v-ai/custom-prompts',
            name: 'v-ai-admin-panel-custom-prompts',
            component: () => import('@/views/Admin/views/vAI/VAICustomPrompt.vue'),
            meta: {
              pageTitle: 'vAI Custom Prompts',
            },
          },
          {
            path: 'v-ai/email-inboxes',
            name: 'v-ai-admin-panel-email-inboxes',
            component: () => import('@/views/Admin/views/vAI/VAIEmailInboxes.vue'),
            meta: {
              pageTitle: 'vAI Email Inboxes',
            },
          },
          {
            path: 'v-ai/email-inboxes/create',
            name: 'v-ai-admin-panel-email-inbox-create',
            component: () => import('@/views/Admin/views/vAI/VAIEmailInboxConfig.vue'),
            meta: {
              pageTitle: 'Create vAI Email Inbox',
            },
          },
          {
            path: 'v-ai/email-inboxes/:inbox_id(\\d+)/edit',
            name: 'v-ai-admin-panel-email-inbox-edit',
            component: () => import('@/views/Admin/views/vAI/VAIEmailInboxConfig.vue'),
            meta: {
              pageTitle: 'Edit vAI Email Inbox',
            },
          },
          // TEMPORARY: redirects old URLs — remove next release. See router/legacyRouteMigrations.js.
          {
            path: 'v-ai/email-inboxes/new',
            redirect: (to) => ({ name: 'v-ai-admin-panel-email-inbox-create', params: to.params }),
          },
          {
            path: 'v-ai/email-inboxes/:inbox_id(\\d+)',
            redirect: (to) => ({ name: 'v-ai-admin-panel-email-inbox-edit', params: to.params }),
          },
          {
            path: 'v-ai/email-logs',
            name: 'v-ai-admin-panel-email-logs',
            component: () => import('@/views/Admin/views/vAI/VAIEmailLogs.vue'),
            meta: {
              pageTitle: 'vAI Email Logs',
            },
          },
          {
            path: 'v-ai/email-logs/:log_id',
            name: 'v-ai-admin-panel-email-log-details',
            component: () => import('@/views/Admin/views/vAI/VAIEmailLogDetails.vue'),
            meta: {
              pageTitle: 'vAI Email Log',
            },
          },
          {
            path: 'v-ai/automations',
            component: () => import('@/views/Admin/views/AutomationsAndValidations/AutomationAndValidationLayout.vue'),
            children: [
              {
                path: '',
                name: 'v-ai-admin-panel-automations',
                component: () => import('@/views/Admin/views/AutomationsAndValidations/index.vue'),
                props: {
                  type: 'automation',
                  storageKey: 'vai-automations',
                  breadcrumbRoutes: [
                    { href: { name: 'admin-panel' }, disabled: false, text: 'Admin' },
                    { href: { name: 'v-ai-admin-panel' }, disabled: false, text: 'vAI' },
                    { href: {}, disabled: true, text: 'vAI Automations' },
                  ],
                  lockedFilters: [
                    {
                      key: 'actions',
                      disabled: true,
                      operator: 'in',
                      entity: 'automations.actions',
                      field: 'slug',
                      values: {
                        value: ['runVaiCustomPrompt', 'launchVaiBrowserAutomation'],
                      },
                    },
                  ],
                },
                meta: {
                  pageTitle: 'vAI Automations',
                },
              },
            ],
          },
          {
            path: 'v-ai/browser-automations',
            name: 'v-ai-admin-panel-browser-automations',
            component: () => import('@/views/Admin/views/vAI/VAIBrowserAutomations.vue'),
            meta: {
              pageTitle: 'vAI Browser Automations',
            },
          },
          {
            path: 'v-ai/browser-automations/create',
            name: 'v-ai-admin-panel-browser-automation-create',
            component: () => import('@/views/Admin/views/vAI/VAIBrowserAutomationConfig.vue'),
            meta: {
              pageTitle: 'Create vAI Browser Automation',
            },
          },
          {
            path: 'v-ai/browser-automations/:browser_automation_id(\\d+)/edit',
            name: 'v-ai-admin-panel-browser-automation-edit',
            component: () => import('@/views/Admin/views/vAI/VAIBrowserAutomationConfig.vue'),
            meta: {
              pageTitle: 'Edit vAI Browser Automation',
            },
          },
          // TEMPORARY: redirects old URLs — remove next release. See router/legacyRouteMigrations.js.
          {
            path: 'v-ai/browser-automations/new',
            redirect: (to) => ({ name: 'v-ai-admin-panel-browser-automation-create', params: to.params }),
          },
          {
            path: 'fields-and-sections',
            name: 'admin-fields-and-sections',
            component: () => import('@/views/Admin/views/FieldsAndSections.vue'),
            meta: {
              pageTitle: 'Edit Fields and Sections',
              pageDescription: 'You’re currently making System Wide Changes. Changes made here will impact ALL Records',
            },
          },
          {
            path: 'import-or-update-records/create',
            name: 'admin-import-or-update-records',
            component: () => import('@/views/Admin/views/ImportUpdateRecords.vue'),
          },
          {
            path: 'import-or-update-records',
            name: 'admin-import-or-update-status',
            component: () => import('@/views/Admin/views/ImportUpdateStatus.vue'),
            meta: {
              pageTitle: 'Import Status',
            },
          },
          {
            path: 'file-types',
            name: 'admin-file-types',
            component: () => import('@/views/Admin/views/FileTypes.vue'),
            meta: {
              pageTitle: 'File Types',
              pageDescription: 'Create or Update File Types.',
            },
          },
          {
            path: 'file-templates',
            name: 'admin-file-templates',
            component: () => import('@/views/Admin/views/FileTemplates.vue'),
            meta: {
              pageTitle: 'File Templates',
              pageDescription: () => h('div', [
                'Create pre-filled documents like quotes, BOLs, or contracts using Vsimple data. Click',
                h('a', {
                  href: "https://docs.vsimple.com/hc/vsimple/en/categories/file-templates",
                  rel: "noreferrer noopener",
                  target: "_blank",
                }, [' here ']),
                'or message us to learn more!',
              ]),
            },
          },
          {
            path: 'statuses',
            name: 'admin-statuses',
            component: () => import('@/views/Admin/views/Statuses.vue'),
            meta: {
              pageTitle: 'Statuses',
              pageDescription: 'Create or Update Record Statuses',
            },
          },
          {
            path: 'basic-info',
            name: 'admin-basic-info',
            component: () => import('@/views/Admin/views/WorkflowInfo.vue'),
            meta: {
              pageTitle: 'Basic Info',
            },
          },
          {
            path: 'naming-conventions-and-help-text',
            name: 'admin-naming-conventions',
            component: () => import('@/views/Admin/views/NamingConventionsAndHelpText.vue'),
            meta: {
              pageTitle: 'Naming Conventions and Help Text',
              pageDescription: 'Configure record naming conventions and help text for name, description, and collaborator fields.',
            },
          },
          {
            path: 'automations',
            component: () => import('@/views/Admin/views/AutomationsAndValidations/AutomationAndValidationLayout.vue'),
            children: [
              {
                path: '',
                name: 'admin-automations',
                component: () => import('@/views/Admin/views/AutomationsAndValidations/index.vue'),
                props: {
                  type: 'automation',
                },
                meta: {
                  pageTitle: 'Automations',
                  pageDescription: 'Manage your Vsimple automations here.',
                },
              },
              {
                path: 'create',
                name: 'admin-automation-create',
                component: () => import('@/views/Admin/views/AutomationsAndValidations/AutomationAndValidationForm.vue'),
                props: {
                  type: 'automation',
                },
                meta: {
                  pageTitle: 'Create Automation',
                },
              },
              {
                path: ':automation_id(\\d+)/edit',
                name: 'admin-automation-edit',
                component: () => import('@/views/Admin/views/AutomationsAndValidations/AutomationAndValidationForm.vue'),
                props: {
                  type: 'automation',
                },
                meta: {
                  pageTitle: 'Edit Automation',
                },
              },
              // TEMPORARY: redirects old URLs — remove next release. See router/legacyRouteMigrations.js.
              {
                path: 'new',
                redirect: (to) => ({ name: 'admin-automation-create', params: to.params }),
              },
              {
                path: ':automation_id(\\d+)',
                redirect: (to) => ({ name: 'admin-automation-edit', params: to.params }),
              },
            ],
          },
          {
            path: 'validations',
            component: () => import('@/views/Admin/views/AutomationsAndValidations/AutomationAndValidationLayout.vue'),
            children: [
              {
                path: '',
                name: 'admin-validations',
                component: () => import('@/views/Admin/views/AutomationsAndValidations/index.vue'),
                props: {
                  type: 'validation',
                },
                meta: {
                  pageTitle: 'Validations',
                  pageDescription: 'Prevent unwanted behavior',
                },
              },
              {
                path: 'create',
                name: 'admin-validation-create',
                component: () => import('@/views/Admin/views/AutomationsAndValidations/AutomationAndValidationForm.vue'),
                props: {
                  type: 'validation',
                },
                meta: {
                  pageTitle: 'Create Validation',
                },
              },
              {
                path: ':validation_id(\\d+)/edit',
                name: 'admin-validation-edit',
                component: () => import('@/views/Admin/views/AutomationsAndValidations/AutomationAndValidationForm.vue'),
                props: {
                  type: 'validation',
                },
                meta: {
                  pageTitle: 'Edit Validation',
                },
              },
              // TEMPORARY: redirects old URLs — remove next release. See router/legacyRouteMigrations.js.
              {
                path: 'new',
                redirect: (to) => ({ name: 'admin-validation-create', params: to.params }),
              },
              {
                path: ':validation_id(\\d+)',
                redirect: (to) => ({ name: 'admin-validation-edit', params: to.params }),
              },
            ],
          },
          {
            path: 'record-types',
            name: 'admin-record-types',
            component: () => import('@/views/Admin/views/RecordTypes.vue'),
            meta: {
              pageTitle: 'Record Types',
              pageDescription: 'Record Types are used to hide irrelevant sections from your Vsimple records',
            },
          },
          {
            path: 'integrations',
            name: 'admin-integrations',
            component: () => import('@/views/Admin/views/Integrations.vue'),
            meta: {
              pageTitle: 'Integrations',
              pageDescription: 'View all the organizations integrations to 3rd party systems',
            },
          },
          {
            path: 'integrations/:integration_id(\\d+)/edit',
            name: 'admin-integration-field-mapping',
            component: () => import('@/views/Admin/views/IntegrationFieldMapping.vue'),
            meta: {
              pageTitle: 'Edit Integration Config',
              pageDescription: () => h('div', [
                'Here you can manage integration field mapping config.',
                h('a', {
                  href: "https://chatwoot.help/hc/vsimple/articles/1707739701-integration-admin-page",
                  rel: "noreferrer noopener",
                  target: "_blank",
                }, [' Learn more here!']),
              ]),
            },
          },
          {
            path: 'v-forms',
            name: 'admin-v-forms',
            component: () => import('@/views/Admin/views/VForms/VForms.vue'),
            meta: {
              pageTitle: 'vForms',
              pageDescription: 'Create and edit forms',
            },
          },
          {
            path: 'v-forms/create',
            name: 'admin-v-form-create',
            component: () => import('@/views/Admin/views/VForms/VFormBuilder.vue'),
            meta: {
              pageTitle: 'Create vForm',
              pageDescription: 'Create a form',
            },
          },
          {
            path: 'v-forms/:form_id/edit',
            name: 'admin-v-form-edit',
            component: () => import('@/views/Admin/views/VForms/VFormBuilder.vue'),
            meta: {
              pageTitle: 'Edit vForm',
              pageDescription: 'Edit a form',
            },
          },
          // TEMPORARY: redirects old URLs — remove next release. See router/legacyRouteMigrations.js.
          {
            path: 'v-forms/builder/:form_id?',
            redirect: (to) => (
              to.params.form_id
                ? { name: 'admin-v-form-edit', params: to.params }
                : { name: 'admin-v-form-create', params: to.params }
            ),
          },
          {
            path: 'teams',
            name: 'admin-teams',
            component: () => import('@/views/Admin/views/Teams/Teams.vue'),
            meta: {
              pageTitle: 'Teams',
              pageDescription: 'Use Teams in automation notifications, activity stream mentions, or in validations to prevent unwanted behavior',
            },
            children: [],
          },
          {
            path: 'teams/:team_id(\\d+)/edit/users',
            name: 'admin-team',
            component: () => import('@/views/Admin/views/Teams/TeamInfo.vue'),
            meta: {
              pageTitle: 'Team Users',
              pageDescription: 'Use Teams in automation notifications, activity stream mentions, or in validations to prevent unwanted behavior',
            },
          },
          // TEMPORARY: redirects old URLs — remove next release. See router/legacyRouteMigrations.js.
          {
            path: 'teams/:team_id(\\d+)?/users',
            redirect: (to) => (
              to.params.team_id
                ? { name: 'admin-team', params: to.params }
                : { name: 'admin-teams' }
            ),
          },
          {
            path: 'dashboard',
            name: 'admin-dashboard',
            component: () => import('@/views/Admin/views/Dashboard/AdminDashboard.vue'),
            meta: {
              pageTitle: 'Dashboard',
            },
          },
          {
            path: 'recycle-bin',
            name: 'admin-recycle-bin',
            component: () => import('@/views/Admin/views/RecycleBin.vue'),
            meta: {
              pageTitle: 'Recycle Bin',
            },
          },
          {
            path: 'outlook-integration',
            name: 'admin-outlook-integration',
            component: () => import('@/views/Admin/views/OutlookIntegration.vue'),
            meta: {
              pageTitle: 'Outlook Integration',
            },
          },
          {
            path: '*',
            redirect: { name: 'admin-panel' },
          },
        ],
      },
      {
        path: 'users',
        name: 'users',
        component: () => import('@/views/Users/index.vue'),
        meta: {
          middleware: [auth, internalAdmin],
          pageTitle: 'User Management',
        },
      },
      {
        path: 'users/:user_id(\\d+)/edit',
        name: 'user-edit',
        props: { isEdit: true },
        component: () => import('@/views/UserSettings/index.vue'),
        meta: {
          middleware: auth,
          pageTitle: 'Edit User',
          pageDescription: 'Change user’s settings',
        },
      },
      {
        path: 'users/create',
        name: 'user-create',
        component: () => import('@/views/UserSettings/index.vue'),
        meta: {
          middleware: auth,
          pageTitle: 'Create User',
          pageDescription: 'Create a new user',
        },
      },
      // TEMPORARY: redirects old URLs — remove next release. See router/legacyRouteMigrations.js.
      {
        path: 'users/:user_id(\\d+)/settings',
        redirect: (to) => ({ name: 'user-edit', params: to.params }),
      },
    ],
  },
  {
    path: '/:company?/*',
    component: () => import('@/layouts/Default.vue'),
    children: [
      {
        path: '',
        component: () => import('@/views/NotFound.vue'),
        name: 'not-found',
      },
    ],
    meta: {
      middleware: auth,
    },
  },
]

const unavailableRoutes = [
  {
    path: '/:catchAll(.*)',
    component: () => import('@/views/UnderConstruction.vue'),
  },
]

export default import.meta.env.VITE_APP_UNAVAILABLE === 'true' ? unavailableRoutes : routes
